import { useEffect, useRef } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProposalProductItemType, ProductType } from '@econtractor/types';
import { ProductSetsSelect } from 'modules/product';
import { ProductsSelect } from 'modules/product';
import InputQuantity from 'components/others/InputQuantity';
import spinnerTitle from 'components/others/spinnerTitle';
import ProductsTable from './ProductsTable';
import useTemplateProducts from './model/useTemplateProducts';
import useUpdateProductPrices from './model/useUpdateProductPrices';

export interface TemplateProductsProps {
    products: ProposalProductItemType[];
    productsMap: Record<number, ProductType>;
    rate: number;
    localeNs?: string;
    disabled?: boolean;
    onChange: (products: ProposalProductItemType[]) => void;
}

const TemplateProducts = ({
    products,
    productsMap,
    rate,
    localeNs = 'Proposal',
    disabled = false,
    onChange,
}: TemplateProductsProps) => {
    const { t } = useTranslation([localeNs, 'Warehouses', 'common']);
    const quantityRef = useRef<HTMLInputElement>(null);
    const {
        sourceType,
        setSourceType,
        setAddMode,
        setSetAddMode,
        selectedProduct,
        setSelectedProduct,
        selectedSet,
        setSelectedSet,
        draftCount,
        setDraftCountDown,
        setDraftCountUp,
        setDraftCountFromInput,
        canAdd,
        isAdding,
        handleDraftAdd,
        replacedAlert,
        dismissReplacedAlert,
    } = useTemplateProducts({ products, productsMap, rate, onChange });

    const { isUpdating, canUpdate, handleUpdatePrices } = useUpdateProductPrices({
        products,
        rate,
        localeNs,
        onChange,
    });

    const hasSelection =
        sourceType === 'product' ? !!selectedProduct?.value : !!selectedSet?.value;
    const selectionId =
        sourceType === 'product' ? selectedProduct?.value : selectedSet?.value;

    useEffect(() => {
        if (!selectionId) return;
        quantityRef.current?.focus();
    }, [selectionId]);

    return (
        <>
            {!disabled ? (
                <div className="border rounded p-3 mb-3">
                    <div className="btn-group w-100 mb-3">
                        <Button
                            size="sm"
                            variant={sourceType === 'product' ? 'primary' : 'outline-primary'}
                            onClick={() => setSourceType('product')}
                        >
                            <i className="bi bi-box-seam me-1"></i>
                            {t('edit.products.modeProduct')}
                        </Button>
                        <Button
                            size="sm"
                            variant={sourceType === 'set' ? 'primary' : 'outline-primary'}
                            onClick={() => setSourceType('set')}
                        >
                            <i className="bi bi-collection me-1"></i>
                            {t('edit.products.modeSet')}
                        </Button>
                    </div>

                    {sourceType === 'product' ? (
                        <ProductsSelect
                            title={t('edit.products.product')}
                            required={false}
                            value={selectedProduct?.value || null}
                            onChange={(opt) => setSelectedProduct(opt)}
                        />
                    ) : (
                        <ProductSetsSelect
                            title={t('edit.products.set')}
                            required={false}
                            value={selectedSet?.value || null}
                            onChange={(opt) => setSelectedSet(opt)}
                        />
                    )}

                    {hasSelection ? (
                        <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-end">
                            <InputQuantity
                                ref={quantityRef}
                                label={t('edit.products.count')}
                                value={draftCount}
                                onDown={setDraftCountDown}
                                onUp={setDraftCountUp}
                                onChange={setDraftCountFromInput}
                                onEnter={handleDraftAdd}
                            />

                            {sourceType === 'set' ? (
                                <Form.Group className="mb-0">
                                    <Form.Label className="text-muted mb-1 fs-12">{t('edit.products.addAs')}</Form.Label>
                                    <div className="btn-group d-flex">
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={setAddMode === 'set' ? 'primary' : 'outline-primary'}
                                            disabled={isAdding}
                                            onClick={() => setSetAddMode('set')}
                                        >
                                            {t('edit.products.addModeSet')}
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={setAddMode === 'products' ? 'primary' : 'outline-primary'}
                                            disabled={isAdding}
                                            onClick={() => setSetAddMode('products')}
                                        >
                                            {t('edit.products.addModeProducts')}
                                        </Button>
                                    </div>
                                </Form.Group>
                            ) : null}

                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                className="mb-0"
                                disabled={!canAdd || isAdding}
                                onClick={handleDraftAdd}
                            >
                                {spinnerTitle(
                                    <>
                                        <i className="bi bi-plus me-1" />
                                        {t('edit.products.add')}
                                    </>,
                                    isAdding,
                                    t('edit.products.add'),
                                )}
                            </Button>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {replacedAlert?.length ? (
                <Alert variant="warning" dismissible onClose={dismissReplacedAlert} className="text-start mb-3">
                    <div className="mb-1">{t('edit.products.replacedAlert')}</div>
                    <ul className="mb-0 ps-3">
                        {replacedAlert.map((item) => (
                            <li key={`${item.productId}-${item.prodName}`}>
                                {item.prodName || `#${item.productId}`}
                            </li>
                        ))}
                    </ul>
                </Alert>
            ) : null}

            {products.length ? (
                <>
                    {!disabled ? (
                        <div className="d-flex justify-content-end mb-2">
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                disabled={!canUpdate || isUpdating}
                                onClick={() => void handleUpdatePrices()}
                            >
                                {spinnerTitle(
                                    <>
                                        <i className="bi bi-arrow-repeat me-1" />
                                        {t('edit.products.updatePrices')}
                                    </>,
                                    isUpdating,
                                    t('edit.products.updatePrices'),
                                )}
                            </button>
                        </div>
                    ) : null}
                    <ProductsTable
                        products={products}
                        rate={rate}
                        localeNs={localeNs}
                        disabled={disabled}
                        onChange={onChange}
                    />
                </>
            ) : (
                <p className="text-muted mb-0">{t('edit.products.empty')}</p>
            )}
        </>
    );
};

export default TemplateProducts;
